import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApprovalType,
  ApproverKind,
  ApproverScope,
  ContentChannel,
  GlobalApprovalMode,
  ReviewerApproverMode,
  SocialPlatform,
  socialPlatformLabel,
} from '@mg/shared';
import type { ApproverAssignment } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useIsWorkspaceAdmin } from '../../auth/useWorkspaceAdmin';
import { useTenant } from '../../auth/TenantContext';
import { useTenantKey } from '../../lib/useTenantKey';
import { channelLabel } from '../../lib/display';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { EDGE_BLEED_FRAME, useEdgeBleed } from '../../components/ui/useEdgeBleed';
import { useFooterSave } from '../../components/settings/footerSave';

/** Which axis a column belongs to — drives the freeze rules (Campaign vs Post Types/Platforms). */
type ColumnGroup = 'campaign' | 'postType' | 'platform';

/**
 * A matrix column: one approvable value on an axis, carrying its own {@link ApproverScope}.
 * `value === null` is the "All"/CAMPAIGN wildcard (scopeValue null on the server).
 */
interface MatrixColumn {
  group: ColumnGroup;
  scope: ApproverScope;
  label: string;
  value: string | null;
}

/** The standalone left-most column: whole-campaign approval (scope CAMPAIGN, no scopeValue). */
const CAMPAIGN_COLUMN: MatrixColumn = {
  group: 'campaign',
  scope: ApproverScope.CAMPAIGN,
  label: 'Campaign',
  value: null,
};

const POST_TYPE_COLUMNS: MatrixColumn[] = [
  { group: 'postType', scope: ApproverScope.POST_TYPE, label: 'All Types', value: null },
  ...Object.values(ContentChannel).map<MatrixColumn>((c) => ({
    group: 'postType',
    scope: ApproverScope.POST_TYPE,
    label: channelLabel[c],
    value: c,
  })),
];

const PLATFORM_COLUMNS: MatrixColumn[] = [
  { group: 'platform', scope: ApproverScope.PLATFORM, label: 'All Platforms', value: null },
  ...Object.values(SocialPlatform).map<MatrixColumn>((p) => ({
    group: 'platform',
    scope: ApproverScope.PLATFORM,
    label: socialPlatformLabel[p],
    value: p,
  })),
];

/** The two stacked checkboxes per cell: Reviewer (top) then Approver (bottom). */
const KIND_ROWS: { kind: ApproverKind; prefix: string; label: string }[] = [
  { kind: ApproverKind.REVIEWER, prefix: 'R', label: 'Reviewer' },
  { kind: ApproverKind.APPROVER, prefix: 'A', label: 'Approver' },
];

/** Stable key for a (scope, scopeValue, kind) cell — the wildcard collapses to `*`. */
function cellKey(scope: ApproverScope, value: string | null, kind: ApproverKind): string {
  return `${scope}:${value ?? '*'}:${kind}`;
}

/** Per-member cell key: `${userId}:${scope}:${value}:${kind}` — the draft map's key. */
function fullCellKey(
  userId: string,
  scope: ApproverScope,
  value: string | null,
  kind: ApproverKind,
): string {
  return `${userId}:${cellKey(scope, value, kind)}`;
}

/** A pending (unsaved) checkbox change, held in the draft until the user clicks Save. */
interface DraftChange {
  userId: string;
  scope: ApproverScope;
  value: string | null;
  kind: ApproverKind;
  desired: boolean;
}

/** GlobalApprovalMode → the workspace-level ApprovalType it forces (null = defer to workspace). */
function globalToApproval(mode: GlobalApprovalMode): ApprovalType | null {
  switch (mode) {
    case GlobalApprovalMode.CAMPAIGN:
      return ApprovalType.CAMPAIGN;
    case GlobalApprovalMode.POST:
      return ApprovalType.POST;
    case GlobalApprovalMode.NONE:
      return ApprovalType.NONE;
    case GlobalApprovalMode.PER_WORKSPACE:
      return null;
  }
}

/** Shared behaviour every {@link AxisCell} needs, threaded down from the card. */
interface CellContext {
  cellChecked: (
    userId: string,
    scope: ApproverScope,
    value: string | null,
    kind: ApproverKind,
  ) => boolean;
  setCell: (
    userId: string,
    scope: ApproverScope,
    value: string | null,
    kind: ApproverKind,
    desired: boolean,
  ) => void;
  isInteractive: (group: ColumnGroup, kind: ApproverKind) => boolean;
  isAdmin: boolean;
  saving: boolean;
}

/**
 * One member×column cell: the stacked Reviewer/Approver checkboxes. Hoisted to module scope (not
 * nested inside the card) so its component identity is stable — a component declared in the card's
 * render body would get a new identity every render, forcing React to remount every cell on each
 * toggle and dropping the focused checkbox. State is threaded in via {@link CellContext}.
 */
function AxisCell({
  userId,
  column,
  isFirst,
  cellChecked,
  setCell,
  isInteractive,
  isAdmin,
  saving,
}: CellContext & { userId: string; column: MatrixColumn; isFirst: boolean }) {
  return (
    <td className={`px-2 py-1.5 align-middle ${isFirst ? 'border-l border-line' : ''}`}>
      <div className="flex flex-col items-center gap-1">
        {KIND_ROWS.map(({ kind, prefix, label }) => {
          const own = cellChecked(userId, column.scope, column.value, kind);
          // A SPECIFIC post-type/platform cell is COVERED when its axis "All …" wildcard is
          // checked (draft or saved) for this member+kind — the wildcard grants authority over
          // every column on the axis, so show those cells checked + disabled (uncheck "All …" to
          // change one). Reading the DRAFT here is what makes ticking "All Types" light up every
          // associated column immediately.
          const coveredByAll =
            column.value !== null &&
            (column.group === 'postType' || column.group === 'platform') &&
            cellChecked(userId, column.scope, null, kind);
          // A frozen cell blocks CHECKING a grant that wouldn't apply, but an admin can always
          // UNCHECK one that's currently on (unchecking only removes authority) — otherwise
          // stale/legacy grants in a now-frozen column would be unrevocable from the matrix. Once
          // unchecked, `own` is false so the box re-disables and can't be re-checked while frozen.
          const interactive =
            !coveredByAll && (isInteractive(column.group, kind) || (isAdmin && own));
          return (
            <label
              key={kind}
              className="flex items-center gap-1 text-[0.625rem] font-semibold leading-none text-ink-faint"
            >
              <span aria-hidden>{prefix}</span>
              <input
                type="checkbox"
                className="focus-ring h-3.5 w-3.5 rounded border-line-input text-action disabled:opacity-40"
                checked={own || coveredByAll}
                disabled={!interactive || saving}
                title={coveredByAll ? `Covered by “${column.group === 'platform' ? 'All Platforms' : 'All Types'}”` : undefined}
                aria-label={`${label} — ${column.label}`}
                onChange={() => setCell(userId, column.scope, column.value, kind, !own)}
              />
            </label>
          );
        })}
      </div>
    </td>
  );
}

/**
 * Approver matrix. One row per workspace member; columns for whole **Campaigns** (left-most),
 * **Post Types** (incl. "All Types"), and **Platforms** (incl. "All Platforms"). Each cell holds
 * two stacked checkboxes — **Reviewer** (first-stage) and **Approver** (final) — each an
 * {@link ApproverAssignment} identified by (scope, scopeValue, kind). A specific value pins one
 * platform/post-type; an "All" column is the wildcard. "Any one" assignee at the required stage
 * is sufficient.
 *
 * Which columns actually apply is driven by the effective approval mode (install-wide
 * `globalApprovalMode` overriding the workspace's own `approvalType`) and the install-wide
 * `reviewerApproverMode`. Frozen cells stay visible with their stored values but are disabled
 * ("shown but don't apply"). Toggling an active cell creates/deletes the assignment and
 * invalidates the list so the grid reflects server truth. Non-admins see it read-only (the API
 * enforces the write gate; the disabled inputs just avoid sure-fire 403s).
 *
 * On the Brand Workspace v2 Workflow page (inside a FooterSaveProvider) the matrix is saved by the
 * page's sticky Save footer, LAST of the page's three (`order` 30, after the approval settings and
 * the image limit, E39: it sits inside the Approvals section, so without an order its effect would
 * register, and save, first), and "Save role assignments" is hidden. Cells that fail stay in the
 * draft. Everywhere else the registration does nothing. `id` and `canEdit` are that page's too.
 */
export function ApproverAssignmentsCard({
  approvalTypeOverride,
  reviewerModeOverride,
  id,
  canEdit,
}: {
  /** Live (possibly-unsaved) workspace approval mode from the Approvals section's dropdown. */
  approvalTypeOverride?: ApprovalType;
  /** Live (possibly-unsaved) workspace reviewer flow — unlocks/locks the R column immediately. */
  reviewerModeOverride?: ReviewerApproverMode;
  /** The card's id: the v2 Workflow page's "Approval role assignment" section. */
  id?: string;
  /** Who may change the assignments. Default: today's rule, useIsWorkspaceAdmin. */
  canEdit?: boolean;
} = {}) {
  const tenantKey = useTenantKey();
  const queryClient = useQueryClient();
  const isWorkspaceAdmin = useIsWorkspaceAdmin();
  const isAdmin = canEdit ?? isWorkspaceAdmin;
  const { activeTenant } = useTenant();

  const assignmentsQuery = useQuery({
    queryKey: ['approver-assignments', tenantKey],
    queryFn: ({ signal }) => api.listApproverAssignments(signal),
    enabled: tenantKey !== '',
  });
  const membersQuery = useQuery({
    queryKey: ['members', tenantKey],
    queryFn: ({ signal }) => api.listMembers(signal),
    enabled: tenantKey !== '',
  });
  // Install-wide approval policy: which columns apply + whether the Reviewer stage runs.
  const platformQuery = useQuery({
    queryKey: ['platform-settings'],
    queryFn: ({ signal }) => api.getPlatformSettings(signal),
  });

  const members = membersQuery.data ?? [];
  // On a desktop screen too narrow for the matrix, the whole card runs off the page's right edge and
  // the matrix reaches through the card's padding to that edge, so the cut says it scrolls.
  const bleedRef = useEdgeBleed<HTMLDivElement>('marked');

  // The mode that governs this workspace: the install-wide global mode wins unless it defers
  // (PER_WORKSPACE), in which case the workspace's own approvalType applies. Undefined while the
  // queries load — we then treat every column as active (writes are valid regardless of mode;
  // the freeze is only a "these don't apply" hint), defaulting to today's APPROVER_ONLY.
  const globalMode = platformQuery.data?.globalApprovalMode;
  // Workspace-level values — a live override from the parent Approvals section wins over the saved
  // tenant, so changing a dropdown updates the freeze/unlock immediately (before Save).
  const workspaceApprovalType = approvalTypeOverride ?? activeTenant?.approvalType;
  const workspaceReviewerMode = reviewerModeOverride ?? activeTenant?.reviewerApproverMode;
  const effectiveMode: ApprovalType | undefined =
    globalMode === undefined
      ? workspaceApprovalType
      : (globalToApproval(globalMode) ?? workspaceApprovalType);
  // Effective reviewer flow — mirrors approval: the install-wide mode wins UNLESS it defers
  // (PER_WORKSPACE), in which case the workspace's own flow applies. (Reading ONLY the global value
  // was the bug — a PER_WORKSPACE install ignored the workspace's "Reviewer then Approver", so the
  // R checkboxes never unlocked.)
  const globalReviewerMode = platformQuery.data?.reviewerApproverMode;
  const effectiveReviewerMode: ReviewerApproverMode | undefined =
    globalReviewerMode === undefined
      ? workspaceReviewerMode
      : globalReviewerMode === ReviewerApproverMode.PER_WORKSPACE
        ? workspaceReviewerMode
        : globalReviewerMode;
  const reviewerStageActive =
    (effectiveReviewerMode ?? ReviewerApproverMode.APPROVER_ONLY) === ReviewerApproverMode.BOTH;

  /** Whether a column group applies (is active) under the effective mode. */
  function groupActive(group: ColumnGroup): boolean {
    if (effectiveMode === ApprovalType.NONE) return false;
    if (effectiveMode === undefined) return true; // still loading — don't freeze on mode
    return group === 'campaign'
      ? effectiveMode === ApprovalType.CAMPAIGN
      : effectiveMode === ApprovalType.POST;
  }
  /** A checkbox is interactive only when admin, its column applies, and its stage is enabled. */
  function isInteractive(group: ColumnGroup, kind: ApproverKind): boolean {
    if (!isAdmin) return false;
    if (!groupActive(group)) return false;
    if (kind === ApproverKind.REVIEWER && !reviewerStageActive) return false;
    return true;
  }

  const campaignActive = groupActive('campaign');
  const axisActive = groupActive('postType'); // Post Types + Platforms share the same activeness.

  // userId → (cellKey → assignment), keyed by (scope, scopeValue, kind) across every scope
  // (CAMPAIGN rows are the left-most column; legacy CAMPAIGN grants have kind APPROVER and show
  // as a checked Campaign/Approver box).
  const byUser = useMemo(() => {
    const map = new Map<string, Map<string, ApproverAssignment>>();
    for (const a of assignmentsQuery.data ?? []) {
      const inner = map.get(a.approverUserId) ?? new Map<string, ApproverAssignment>();
      inner.set(cellKey(a.scope, a.scopeValue, a.kind), a);
      map.set(a.approverUserId, inner);
    }
    return map;
  }, [assignmentsQuery.data]);

  // Unsaved edits accumulate here (keyed by fullCellKey) and only hit the API on Save — so the
  // matrix now has its own Save button rather than persisting on every click.
  const [draft, setDraft] = useState<ReadonlyMap<string, DraftChange>>(new Map());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['approver-assignments', tenantKey] });

  const dirty = draft.size > 0;

  /** Whether a cell is checked — the pending draft wins over server truth. */
  function cellChecked(
    userId: string,
    scope: ApproverScope,
    value: string | null,
    kind: ApproverKind,
  ): boolean {
    const d = draft.get(fullCellKey(userId, scope, value, kind));
    if (d) return d.desired;
    return Boolean(byUser.get(userId)?.get(cellKey(scope, value, kind)));
  }

  /** Stage a checkbox change locally (no API call until Save). Toggling a cell back to its saved
   *  value drops the draft entry, so `dirty` reflects only real pending changes. */
  function setCell(
    userId: string,
    scope: ApproverScope,
    value: string | null,
    kind: ApproverKind,
    desired: boolean,
  ) {
    setSaved(false);
    setDraft((prev) => {
      const next = new Map(prev);
      const key = fullCellKey(userId, scope, value, kind);
      const server = Boolean(byUser.get(userId)?.get(cellKey(scope, value, kind)));
      if (desired === server) next.delete(key);
      else next.set(key, { userId, scope, value, kind, desired });
      return next;
    });
  }

  /** Apply one staged change: create the grant if newly-checked, delete it if newly-unchecked. */
  async function applyChange(c: DraftChange): Promise<void> {
    const server = byUser.get(c.userId)?.get(cellKey(c.scope, c.value, c.kind));
    if (c.desired && !server) {
      await api.saveApproverAssignment({
        approverUserId: c.userId,
        scope: c.scope,
        // Omit scopeValue for the "All"/CAMPAIGN wildcard (server stores null).
        scopeValue: c.value ?? undefined,
        kind: c.kind,
      });
    } else if (!c.desired && server) {
      await api.deleteApproverAssignment(server.id);
    }
  }

  /** Saves the draft; resolves to whether every change saved (true when there was nothing to save). */
  async function handleSave(): Promise<boolean> {
    const changes = [...draft.values()];
    if (changes.length === 0) return true;
    if (saving) return false;
    setSaving(true);
    setError(null);
    setSaved(false);
    // Fire every create/delete; keep any that fail in the draft so the user can retry just those
    // (after the refetch, the succeeded ones already match server truth and are pruned).
    const results = await Promise.allSettled(changes.map(applyChange));
    const failed = changes.filter((_, i) => results[i].status === 'rejected');
    await invalidate();
    setDraft(new Map(failed.map((c) => [fullCellKey(c.userId, c.scope, c.value, c.kind), c])));
    if (failed.length > 0) {
      const firstErr = results.find((r) => r.status === 'rejected') as
        | PromiseRejectedResult
        | undefined;
      const reason =
        firstErr?.reason instanceof ApiRequestError
          ? firstErr.reason.message
          : 'Could not save some approver changes.';
      setError(failed.length > 1 ? `${reason} (${failed.length} changes not saved)` : reason);
    } else {
      setSaved(true);
    }
    setSaving(false);
    return failed.length === 0;
  }

  const { inFooter } = useFooterSave({
    id: 'approver-assignments',
    order: 30,
    dirty,
    canSave: isAdmin,
    saving,
    save: handleSave,
    discard: () => {
      setDraft(new Map());
      setError(null);
    },
  });

  // Props every cell needs, bundled once. AxisCell lives at MODULE scope (not nested here) so its
  // component identity is stable across re-renders — a nested component gets a fresh identity each
  // render, which makes React unmount/remount every cell on each toggle and drops keyboard focus.
  const cellProps = { cellChecked, setCell, isInteractive, isAdmin, saving };

  let modeNote: string;
  switch (effectiveMode) {
    case ApprovalType.CAMPAIGN:
      modeNote =
        'This workspace approves at the Campaign level — post-type and platform rows are shown but don’t apply.';
      break;
    case ApprovalType.POST:
      modeNote =
        'This workspace approves per post — the Campaign column is shown but doesn’t apply.';
      break;
    case ApprovalType.NONE:
      modeNote = 'Approvals are turned off for this workspace — nothing here applies.';
      break;
    default:
      modeNote = 'Loading the approval policy…';
  }

  return (
    <Card {...EDGE_BLEED_FRAME} id={id} tabIndex={id ? -1 : undefined} className={id ? 'scroll-mt-6' : undefined}>
      <CardHeader
        title="Approval Role Assignment"
        description="Assign each member as a Reviewer or Approver for whole campaigns, post types, or platforms, then Save. Any one assignee at the required stage is enough; which columns apply depends on this workspace’s approval mode."
      />
      <CardBody className="space-y-4">
        <div className="space-y-1 text-xs text-ink-muted">
          <p>
            <span className="font-semibold text-ink-label">R</span> = Reviewer ·{' '}
            <span className="font-semibold text-ink-label">A</span> = Approver. “All Types” / “All
            Platforms” cover every column on their axis.
          </p>
          <p className={effectiveMode === ApprovalType.NONE ? 'text-amber-600 dark:text-warning-dark' : undefined}>
            {modeNote}
          </p>
          {!reviewerStageActive && (
            <p>Reviewer (R) checkboxes are turned off install-wide (Approver only).</p>
          )}
        </div>

        {assignmentsQuery.isLoading || membersQuery.isLoading || platformQuery.isLoading ? (
          // Wait for platform-settings too, so effectiveMode is final before any cell renders —
          // otherwise a global-override-vs-workspace mismatch could briefly show wrong freeze state.
          <p className="text-sm text-ink-faint">Loading approvers…</p>
        ) : membersQuery.isError ? (
          <p className="text-sm text-red-600 dark:text-error-dark">Could not load workspace members.</p>
        ) : assignmentsQuery.isError ? (
          <p className="text-sm text-red-600 dark:text-error-dark">Could not load approver assignments.</p>
        ) : members.length === 0 ? (
          <p className="text-sm text-ink-muted">
            No workspace members yet. Invite teammates first, then assign approvers here.
          </p>
        ) : (
          <div ref={bleedRef} className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-line">
                  <th
                    rowSpan={2}
                    className="sticky left-0 z-10 bg-card px-3 py-2 text-left align-bottom font-medium text-ink-strong"
                  >
                    Member
                  </th>
                  <th
                    rowSpan={2}
                    className={`whitespace-nowrap border-l border-line px-2 py-2 text-center align-bottom text-xs font-medium text-ink-label ${
                      campaignActive ? '' : 'opacity-40'
                    }`}
                  >
                    Campaign
                  </th>
                  <th
                    colSpan={POST_TYPE_COLUMNS.length}
                    className={`border-l border-line px-2 py-1.5 text-center text-xs font-semibold uppercase tracking-wide text-ink-muted ${
                      axisActive ? '' : 'opacity-40'
                    }`}
                  >
                    Post Types
                  </th>
                  <th
                    colSpan={PLATFORM_COLUMNS.length}
                    className={`border-l border-line px-2 py-1.5 text-center text-xs font-semibold uppercase tracking-wide text-ink-muted ${
                      axisActive ? '' : 'opacity-40'
                    }`}
                  >
                    Platforms
                  </th>
                </tr>
                <tr className="border-b border-line">
                  {POST_TYPE_COLUMNS.map((c, i) => (
                    <th
                      key={`pt-${c.value ?? 'all'}`}
                      className={`whitespace-nowrap px-2 py-2 text-center text-xs font-medium text-ink-label ${
                        i === 0 ? 'border-l border-line' : ''
                      } ${axisActive ? '' : 'opacity-40'}`}
                    >
                      {c.label}
                    </th>
                  ))}
                  {PLATFORM_COLUMNS.map((c, i) => (
                    <th
                      key={`pf-${c.value ?? 'all'}`}
                      className={`whitespace-nowrap px-2 py-2 text-center text-xs font-medium text-ink-label ${
                        i === 0 ? 'border-l border-line' : ''
                      } ${axisActive ? '' : 'opacity-40'}`}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.user.id} className="border-b border-line-soft last:border-b-0">
                    <td className="sticky left-0 z-10 bg-card px-3 py-2">
                      <p className="truncate text-sm font-medium text-ink">
                        {m.user.displayName || m.user.email}
                      </p>
                      <p className="truncate text-xs text-ink-muted">{m.user.email}</p>
                    </td>
                    <AxisCell userId={m.user.id} column={CAMPAIGN_COLUMN} isFirst {...cellProps} />
                    {POST_TYPE_COLUMNS.map((c, i) => (
                      <AxisCell
                        key={`pt-${m.user.id}-${c.value ?? 'all'}`}
                        userId={m.user.id}
                        column={c}
                        isFirst={i === 0}
                        {...cellProps}
                      />
                    ))}
                    {PLATFORM_COLUMNS.map((c, i) => (
                      <AxisCell
                        key={`pf-${m.user.id}-${c.value ?? 'all'}`}
                        userId={m.user.id}
                        column={c}
                        isFirst={i === 0}
                        {...cellProps}
                      />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {error && <p className="text-sm text-red-600 dark:text-error-dark">{error}</p>}

        {isAdmin && members.length > 0 && (
          <div className="flex items-center justify-end gap-3 pt-1">
            {saved && !dirty && <span className="text-sm text-success dark:text-success-dark">Saved.</span>}
            {dirty && (
              <button
                type="button"
                className="text-sm text-ink-muted underline-offset-2 hover:underline disabled:opacity-50"
                onClick={() => {
                  setDraft(new Map());
                  setError(null);
                }}
                disabled={saving}
              >
                Discard changes
              </button>
            )}
            {!inFooter && (
              <Button loading={saving} disabled={!dirty} onClick={() => void handleSave()}>
                Save role assignments
              </Button>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
